/**
 * DOCUMENTATION:
 * Component: GCPStorage
 * Purpose: Deep dive into Cloud SQL, Spanner, Bigtable, and Cloud Storage.
 * * * Note for Junior Developers:
 * - Relational (SQL): Data has a strict schema and relationships (Tables).
 * - NoSQL: Flexible schema, designed for massive scale.
 * - Object Storage: For files, not structured data.
 * - ACID vs. BASE: Spanner is unique because it provides ACID at global scale.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip
} from "@mui/material";

/* Icons */
import StorageIcon from '@mui/icons-material/Storage';
import LanIcon from '@mui/icons-material/Lan';
import SpeedIcon from '@mui/icons-material/Speed';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import SecurityIcon from '@mui/icons-material/Security';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 65, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.2)",
    color: "white",
}));

const StorageFeature = styled(Box)(({ theme, accent }) => ({
    padding: theme.spacing(2),
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    borderLeft: `4px solid ${accent || "#4285F4"}`,
    borderRadius: "4px",
    height: "100%",
}));

export default function GCPStorage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h2"} fontWeight={900} color="#4285F4">
                    STORAGE & DATABASES
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>The Source of Truth</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>

                {/* 1. Decision Flow */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <LanIcon sx={{ color: "#4285F4", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Architectural Decision Tree</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                            Choosing a database starts with a single question: **Is your data structured or unstructured?**
                        </Typography>



                        <TableContainer component={Paper} sx={{ bgcolor: "rgba(0,0,0,0.2)", mt: 4 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>Service</TableCell>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>Type</TableCell>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>Best For...</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ color: "white", fontWeight: 700 }}>Cloud Storage</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>Object</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>Images, videos, backups, data lake files.</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white", fontWeight: 700 }}>Cloud SQL</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>Relational</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>Standard web apps, CMS, ERP (MySQL, Postgres, SQL Server).</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white", fontWeight: 700 }}>Cloud Spanner</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>Relational</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>Global supply chains, banking, global consistency.</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white", fontWeight: 700 }}>Cloud Bigtable</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>NoSQL</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>IoT, AdTech, Fintech (High throughput/Low latency).</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white", fontWeight: 700 }}>Firestore</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>NoSQL</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>Mobile/Web app state, hierarchy data.</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionCard>
                </Grid>

                {/* 2. Cloud Spanner vs Cloud SQL */}
                <Grid item xs={12} md={7}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <StorageIcon sx={{ color: "#34A853" }} />
                            <Typography variant="h5" fontWeight={800}>The Relational Giants</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.8 }}>
                            The exam will test your ability to differentiate these two.
                            If you need **Horizontal Scaling** for a SQL database, Spanner is the only answer.
                        </Typography>



                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={6}>
                                <StorageFeature accent="#4285F4">
                                    <Typography variant="subtitle2" color="#4285F4">Cloud SQL</Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                        - Regional only.<br/>
                                        - Vertical scaling (bigger VM).<br/>
                                        - Read replicas for read scaling.
                                    </Typography>
                                </StorageFeature>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <StorageFeature accent="#34A853">
                                    <Typography variant="subtitle2" color="#34A853">Cloud Spanner</Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                        - Regional or Global.<br/>
                                        - Horizontal scaling (add nodes).<br/>
                                        - Unlimited capacity.
                                    </Typography>
                                </StorageFeature>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 3. Cloud Storage Classes */}
                <Grid item xs={12} md={5}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <FolderZipIcon sx={{ color: "#FBBC04" }} />
                            <Typography variant="h5" fontWeight={800}>Object Lifecycle</Typography>
                        </Stack>



                        <Stack spacing={2} mt={2}>
                            <Box sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2">Standard</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>Frequent access (Website assets).</Typography>
                            </Box>
                            <Box sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2">Nearline</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>Accessed once a month (Monthly reports).</Typography>
                            </Box>
                            <Box sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2">Coldline</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>Accessed once a quarter (Disaster recovery).</Typography>
                            </Box>
                            <Box sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2">Archive</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>Accessed once a year (Regulatory compliance).</Typography>
                            </Box>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* 4. NoSQL & High Throughput */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <SpeedIcon sx={{ color: "#EA4335" }} />
                            <Typography variant="h5" fontWeight={800}>Bigtable: The Performance King</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                            Cloud Bigtable is the same engine that powers Google Search and Maps.
                            It is a **Sparse, Wide-Column** store designed for billions of rows.
                        </Typography>



                        <Alert severity="info" sx={{ mt: 2, bgcolor: "rgba(66, 133, 244, 0.1)", color: "white", border: "1px solid #4285F4" }}>
                            <strong>PCA ARCHITECT SCENARIO:</strong> "You are collecting sensor data from 10 million smart meters every minute. You need sub-10ms latency for ingestion and analytical integration."
                            <br/><br/>
                            <strong>Solution:</strong> Use <strong>Cloud Bigtable</strong>. It handles massive throughput and integrates natively with BigQuery and Dataflow for analytics.
                        </Alert>
                    </SectionCard>
                </Grid>

                {/* Navigation */}
                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
